import {
  Text,
  HStack,
  Box,
  Image,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import React from 'react';

import type { NavGroupItem } from 'types/client/navigation';

import { useI18n } from 'lib/i18n/context';
import Popover from 'ui/shared/chakra/Popover';

import LightningLabel from '../LightningLabel';
import NavLinkIcon from '../NavLinkIcon';
import useNavLinkStyleProps from '../useNavLinkStyleProps';
import { checkRouteHighlight } from '../utils';
import NavLink from './NavLink';

type Props = {
  item: NavGroupItem;
  isCollapsed?: boolean;
};

const NavLinkGroup = ({ item, isCollapsed }: Props) => {
  const { t } = useI18n();
  const isExpanded = isCollapsed === false;

  const styleProps = useNavLinkStyleProps({ isCollapsed, isExpanded, isActive: item.isActive });

  const isHighlighted = checkRouteHighlight(item.subItems);

  const popoverBg = useColorModeValue('#FFFFFF', '#111111');
  const popoverBorder = useColorModeValue('#E9ECEF', '#222222');

  return (
    <Box as="li" listStyleType="none" w="100%">
      <Popover
        trigger="hover"
        placement="right-start"
        // should not be lazy to help google indexing pages
        isLazy={ false }
        gutter={ 8 }
      >
        <PopoverTrigger>
          <Box
            { ...styleProps.itemProps }
            w="192px"
            pl="12px"
            pr="12px"
            aria-label={ t('navigation.textLinkGroup', { text: item.text }) }
            position="relative"
          >
            <HStack spacing={ 0 } overflow="hidden">
              <NavLinkIcon item={ item }/>
              <Text
                { ...styleProps.textProps }
                ml={ 3 }
              >
                { item.text }
              </Text>
              { isHighlighted && (
                <LightningLabel iconColor={ styleProps.itemProps.bgColor } isCollapsed={ isCollapsed }/>
              ) }
              <Image
                src="/static/homepage/sidebar-group-arrow.svg"
                alt=""
                position="absolute"
                right="12px"
                boxSize="16px"
                pointerEvents="none"
              />
            </HStack>
          </Box>
        </PopoverTrigger>
        <PopoverContent
          width="246px"
          top={{ lg: isExpanded ? '-16px' : 0, xl: isCollapsed ? 0 : '-16px' }}
          bg={ popoverBg }
          border="1px solid"
          borderColor={ popoverBorder }
          borderRadius="12px"
          boxShadow="lg"
        >
          <PopoverBody p={ 4 }>
            <Text variant="secondary" fontSize="sm" mb={ 1 } display={{ lg: isExpanded ? 'none' : 'block', xl: isCollapsed ? 'block' : 'none' }}>
              { item.text }
            </Text>
            <VStack spacing={ 1 } alignItems="start" as="ul">
              { item.subItems.map((subItem, index) => Array.isArray(subItem) ? (
                <Box
                  key={ index }
                  w="100%"
                  as="ul"
                  _notLast={{
                    mb: 2,
                    pb: 2,
                    borderBottomWidth: '1px',
                    borderColor: 'divider',
                  }}
                >
                  { subItem.map(subSubItem => <NavLink key={ subSubItem.text } item={ subSubItem } isCollapsed={ false }/>) }
                </Box>
              ) :
                <NavLink key={ subItem.text } item={ subItem } isCollapsed={ false }/>,
              ) }
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default NavLinkGroup;
